import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import Button from '../Ui/Button';
import Input from '../Ui/Input';
import Card from '../Ui/Card';
import { jobRoles, SeekerFormData } from '../types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

interface LocalSeekerRegistrationProps {
    onRegister: (data: SeekerFormData) => void;
    theme: 'light' | 'dark';
}

const SeekerRegistration = ({ onRegister, theme }: LocalSeekerRegistrationProps) => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState<SeekerFormData>({
        name: '',
        email: '',
        phone: '',
        role: '',
        exp: '',
        location: '',
        resumeUrl: ''
    });

    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Form validation function
    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.role.trim()) newErrors.role = 'Job role is required';
        if (!formData.exp.trim()) newErrors.exp = 'Experience is required';
        if (!formData.location.trim()) newErrors.location = 'Location is required';
        if (!resumeFile) newErrors.resume = 'Resume is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Reset form completely including file input
    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            role: '',
            exp: '',
            location: '',
            resumeUrl: ''
        });
        setResumeFile(null);
        setErrors({});

        // Reset file input element
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Check if form is valid
    const isFormValid =
        formData.name.trim() &&
        formData.email.trim() &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
        formData.phone.trim() &&
        formData.role.trim() &&
        formData.exp.trim() &&
        formData.location.trim() &&
        resumeFile;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        if (!isFormValid || !resumeFile) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            setIsSubmitting(true);

            // Convert file to Base64
            const fileToBase64 = (file: File): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = (err) => reject(err);
                    reader.readAsDataURL(file);
                });
            };

            const base64Resume = await fileToBase64(resumeFile);

            // Prepare JSON payload
            const payload = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                role: formData.role,
                exp: formData.exp,
                location: formData.location,
                resumeUrl: base64Resume, // send Base64
            };

            const response = await fetch('https://wehireindia.com/wehire_api/seekers/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            toast.success('Profile created successfully 🎉');

            // Reset form after successful submission
            resetForm();

        } catch (err) {
            toast.error('Something went wrong. Please try again ❌');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle input change with validation
    const handleInputChange = (field: keyof SeekerFormData, value: string) => {
        setFormData({ ...formData, [field]: value });

        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    // Handle file change
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Allowed types
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only PDF or JPG files are allowed');
            return;
        }

        // Max size 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            toast.error('File size cannot exceed 5MB');
            return;
        }

        setResumeFile(file);

        // Convert to Base64
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            setFormData(prev => ({ ...prev, resumeUrl: base64String }));
        };
        reader.readAsDataURL(file);

        // Clear resume error if any
        if (errors.resume) {
            setErrors({ ...errors, resumeUrl: '' });
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-5 px-6">
            <Button variant="text" onClick={() => navigate('/')} theme={theme} className="mb-4 pl-0">
                ← Back to Home
            </Button>
            <Card title="Create Profile" theme={theme}>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-3 items-center gap-4">
                        <div>
                            <Input
                                label="Full Name"
                                name="name"
                                value={formData.name}
                                onChange={e => handleInputChange('name', e.target.value)}
                                placeholder="Enter your full name"
                                required
                                theme={theme}
                            />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <Input
                                label="Email Address"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={e => handleInputChange('email', e.target.value)}
                                placeholder="Enter your email"
                                required
                                theme={theme}
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <Input
                                label="Phone Number"
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={e => handleInputChange('phone', e.target.value)}
                                placeholder="Enter your phone number"
                                required
                                theme={theme}
                            />
                            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        {/* Replace the Input component for job role with this dropdown */}
                        <div className="relative">
                            <label className={`block text-xs font-bold uppercase tracking-wider mb-3 ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                                Job Role *
                            </label>
                            <div className="relative">
                                <select
                                    value={formData.role}
                                    onChange={(e) => handleInputChange('role', e.target.value)}
                                    className={`w-full px-4 py-3 mb-2 rounded-xl text-sm transition-all border-2 focus:outline-none focus:ring-2 appearance-none pr-10 ${theme === 'dark'
                                        ? 'bg-zinc-900 border-zinc-700 text-zinc-100 focus:border-violet-400 focus:ring-violet-400/20'
                                        : 'bg-white border-zinc-200 text-zinc-900 focus:border-violet-500 focus:ring-violet-500/20'
                                        } ${errors.role ? 'border-red-500' : ''}`}
                                    required
                                    size={1} // Single select
                                    style={{
                                        maxHeight: '300px', // Max height for dropdown options
                                        overflowY: 'auto', // Enable vertical scroll
                                    }}
                                >
                                    {jobRoles.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.value === ''}
                                            className={`py-2 px-4 ${theme === 'dark'
                                                ? 'bg-zinc-900 text-zinc-100 hover:bg-zinc-800'
                                                : 'bg-white text-zinc-900 hover:bg-zinc-50'
                                                }`}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                {/* Custom dropdown arrow */}
                                <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'
                                    }`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
                        </div>
                        <div>
                            <Input
                                label="Experience (Yrs)"
                                type="number"
                                name="exp"
                                value={formData.exp}
                                onChange={e => handleInputChange('exp', e.target.value)}
                                placeholder="Years"
                                required
                                theme={theme}
                            />
                            {errors.exp && <p className="text-red-500 text-xs mt-1">{errors.exp}</p>}
                        </div>

                        <div>
                            <Input
                                label="Preferred Location"
                                name="location"
                                value={formData.location}
                                onChange={e => handleInputChange('location', e.target.value)}
                                placeholder="City"
                                required
                                theme={theme}
                            />
                            {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
                        </div>
                    </div>

                    <div className="mb-8">
                        <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${theme === 'dark' ? 'text-zinc-400' : 'text-zinc-500'}`}>
                            Resume Upload *
                        </label>

                        <label
                            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all cursor-pointer group
                                ${theme === 'dark'
                                    ? 'border-zinc-700 bg-zinc-900/50 text-zinc-400 hover:border-violet-400 hover:text-violet-400'
                                    : 'border-zinc-200 bg-zinc-50 text-zinc-400 hover:border-violet-500 hover:text-violet-500'
                                }`}
                        >
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg"
                                hidden
                                onChange={handleFileChange}
                            />

                            <div className={`p-3 rounded-full mb-3 shadow-sm group-hover:scale-110 transition-transform ${theme === 'dark' ? 'bg-zinc-800' : 'bg-white'}`}>
                                <Upload className="w-6 h-6" />
                            </div>

                            <span className="text-sm font-medium">
                                {resumeFile ? resumeFile.name : 'Click to upload PDF/JPG'}
                            </span>
                        </label>
                        {errors.resume && <p className="text-red-500 text-xs mt-1">{errors.resume}</p>}
                    </div>

                    <Button
                        type="submit"
                        fullWidth
                        theme={theme}
                        disabled={!isFormValid || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Launch Profile'}
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default SeekerRegistration;